import React from "react";
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Typography,
} from "@mui/material";
import {
  BarChart,
  Groups as GroupsIcon,
  CheckBox,
  AttachMoney,
} from "@mui/icons-material";
import { useGetLabourDashboardQuery } from "../../../redux/labourTrackerApi";

const NAVY = "#123456";

function fmtMoney(n: number) {
  return `$${n % 1 === 0 ? n : n.toFixed(2)}`;
}

function StatCard({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
}) {
  return (
    <Card sx={{ flex: "1 1 160px", minWidth: 140 }}>
      <CardContent
        sx={{ display: "flex", alignItems: "center", gap: 2, p: 2.5 }}
      >
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: 2,
            bgcolor: `${NAVY}18`,
            color: NAVY,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {icon}
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary" display="block">
            {label}
          </Typography>
          <Typography variant="h5" fontWeight={700}>
            {value}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
}

export const LabourDashboard: React.FC = () => {
  const { data, isLoading } = useGetLabourDashboardQuery();

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={8}>
        <CircularProgress />
      </Box>
    );
  }

  const today = new Date().toLocaleDateString("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
  const cycleLabel = data
    ? `Pay cycle ${data.cycleStart} → ${data.cycleEnd}`
    : "";

  return (
    <Box>
      <Typography variant="h4" fontWeight={700} gutterBottom>
        Dashboard
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        {today} · {cycleLabel}
      </Typography>

      {/* Stat cards */}
      <Box display="flex" gap={2} flexWrap="wrap" mb={4}>
        <StatCard
          icon={<GroupsIcon sx={{ fontSize: 20 }} />}
          label="Active workers"
          value={data?.workerCount ?? "\u2014"}
        />
        <StatCard
          icon={<CheckBox sx={{ fontSize: 20 }} />}
          label="Present today"
          value={data?.todayPresent ?? "\u2014"}
        />
        <StatCard
          icon={<BarChart sx={{ fontSize: 20 }} />}
          label="Active blocks"
          value={data?.blockCount ?? "\u2014"}
        />
        <StatCard
          icon={<AttachMoney sx={{ fontSize: 20 }} />}
          label="Cost this cycle"
          value={data ? fmtMoney(data.cycleCost) : "—"}
        />
      </Box>

      {/* By-block breakdown */}
      <Card>
        <CardContent>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Cumulative cost per block — {data?.cycleStart ?? ""} →{" "}
            {data?.cycleEnd ?? ""}
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Running totals from the 17th of the cycle
          </Typography>
          {!data?.byBlock.length ? (
            <Typography variant="body2" color="text.secondary">
              No attendance recorded this cycle yet.
            </Typography>
          ) : (
            <Box>
              {data.byBlock.map((b, i) => (
                <React.Fragment key={b.name}>
                  {i > 0 && <Divider />}
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    py={1.5}
                  >
                    <Typography fontWeight={500}>{b.name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {b.manDays} man-days
                    </Typography>
                    <Typography fontWeight={700}>{fmtMoney(b.cost)}</Typography>
                  </Box>
                </React.Fragment>
              ))}
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};
